import { useEffect, useRef, useCallback, useMemo } from "react";
import { viewportCategoryToNumericColumnCount } from "../../../state/modules/layout/columnCount";
import {
  createIntersectionCallback,
  createIntersectionObserver,
  checkIntersectionObserverSupport,
  setupRowObservers,
  setupScrollInactivityListener,
} from "../utils";

import { buildProductComponentImpressionEvent } from "../../../helpers/analytics";

import config from "../../../config/common";
import { pushToDataLayerSitechromeWrapper as pushToDataLayer } from "@asosteam/asos-web-datalayer";

const PAGE_SIZE = config.api.product.search.pageSize;
const INTERSECTION_OBSERVER_THRESHOLD = 1;
const SCROLL_INACTIVITY_DELAY = 500;

const useProductListScrollAnalytics = ({
  viewportCategory,
  products,
  pageNumber,
  itemListName,
  localCurrencyCode,
  requestId,
}) => {
  const listRef = useRef(null);
  const observerRef = useRef(null);
  const visibleRowsRef = useRef(new Set());
  const firedIdsRef = useRef(new Set());

  const columnCount = viewportCategoryToNumericColumnCount(viewportCategory);

  const intersectionCallback = useMemo(
    () =>
      createIntersectionCallback({
        visibleRowsRef,
        firedIdsRef,
        products,
        columnCount,
        observerRef,
      }),
    [columnCount, products],
  );

  const fireVisibleRows = useCallback(() => {
    visibleRowsRef.current.forEach((visibleRowId) => {
      const eventData = buildProductComponentImpressionEvent({
        products,
        pageNumber,
        columnCount,
        visibleRowId,
        itemListName,
        localCurrencyCode,
        requestId,
        pageSize: PAGE_SIZE,
      });

      if (eventData) {
        pushToDataLayer({ product_component_impression: eventData });
        firedIdsRef.current.add(visibleRowId);

        if (observerRef.current) {
          const element = listRef.current.querySelector(`#${visibleRowId}`);
          if (element) {
            observerRef.current.unobserve(element);
          }
        }
      }
    });
    visibleRowsRef.current.clear();
  }, [
    products,
    pageNumber,
    columnCount,
    itemListName,
    localCurrencyCode,
    requestId,
    listRef,
  ]);

  const observeChildren = useCallback(() => {
    setupRowObservers({
      listRef,
      products,
      columnCount,
      observerRef,
    });
  }, [listRef, products, columnCount]);

  const shouldUseObserver = useCallback(
    () => checkIntersectionObserverSupport(),
    [],
  );

  useEffect(() => {
    if (!listRef.current || !shouldUseObserver()) return undefined;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observer = createIntersectionObserver(intersectionCallback, {
      threshold: INTERSECTION_OBSERVER_THRESHOLD,
    });
    observerRef.current = observer;

    if (!observer) return undefined;

    observeChildren();

    const cleanupScrollListener =
      setupScrollInactivityListener({
        onScrollInactive: fireVisibleRows,
        inactivityDelay: SCROLL_INACTIVITY_DELAY,
      }) || (() => {});

    return () => {
      cleanupScrollListener();
      observer.disconnect();
    };
  }, [
    shouldUseObserver,
    intersectionCallback,
    observeChildren,
    fireVisibleRows,
  ]);

  return { listRef };
};

export default useProductListScrollAnalytics;
